// /mnt/data/AuroraBackground.jsx

import React, { useRef, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { createNoise2D } from "simplex-noise";

// ————————————————————————————————
// Configuration & Constants
// ————————————————————————————————
const simplex = { noise2D: createNoise2D() };

const CONFIG = {
    // Each band can be tuned independently here:
    bands: [
        {
            colorStart: "#2fffa0",
            colorEnd: "#4de3f7",
            amp: 32,        // vertical noise amplitude
            freq: 0.0015,    // noise frequency
            speed: 0.08,      // time multiplier
            baseY: 0.35,      // base vertical position (fraction of height)
            blur: 48,        // Gaussian blur px
            alpha: 0.18,      // base opacity
            phase: 0,         // random phase offset
            thickness: 120,       // band “height” in px
        },
        {
            colorStart: "#b57fff",
            colorEnd: "#7ad0fc",
            amp: 24,
            freq: 0.0012,
            speed: 0.04,
            baseY: 0.47,
            blur: 32,
            alpha: 0.13,
            phase: 40,
            thickness: 90,
        },
        {
            colorStart: "#ffee70",
            colorEnd: "#3cffb3",
            amp: 17,
            freq: 0.002,
            speed: 0.13,
            baseY: 0.58,
            blur: 56,
            alpha: 0.09,
            phase: 100,
            thickness: 70,
        },
        {
            colorStart: "#ff82f2",
            colorEnd: "#2edaff",
            amp: 11,
            freq: 0.0019,
            speed: 0.10,
            baseY: 0.75,
            blur: 38,
            alpha: 0.08,
            phase: 180,
            thickness: 60,
        },
    ],

    // Horizon glow at bottom
    horizon: {
        start: "rgba(10,24,34,0.5)",
        end: "rgba(0,0,0,0.92)",
    },

    // Starfield density
    starCountLarge: 100,
    starCountSmall: 50,

    // Mobile breakpoint to simplify visuals
    mobileBreakpoint: 600,

    // Canvas slicing for per‑slice drawing
    sliceWidth: 4,
};

// ————————————————————————————————
// Generate a field of stars across the full canvas
// ————————————————————————————————
function generateStars(width, height, count) {
    return Array.from({ length: count }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 1 + Math.random() * 1.5,            // radius
        tw: 0.6 + Math.random() * 0.9,          // twinkle speed
        baseAlpha: 0.3 + Math.random() * 0.4,   // base opacity
    }));
}

// ————————————————————————————————
// Compute vertical centre of a band at x
// ————————————————————————————————
function computeBandY(band, x, t, idx, height) {
    const y0 = height * band.baseY;
    const n1 = simplex.noise2D(x * band.freq + (t * band.speed + band.phase), idx) * band.amp;
    const n2 = simplex.noise2D(
        x * band.freq * 1.3 + (t * band.speed * 0.6 + band.phase * 1.7),
        idx + 7
    ) * band.amp * 0.4;
    return y0 + n1 + n2;
}

// ————————————————————————————————
// React Component
// ————————————————————————————————
const AuroraBackground = () => {
    const canvasRef = useRef(null);
    const { isDarkMode } = useTheme();

    useEffect(() => {
        if (!isDarkMode) return; // only in dark mode

        const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        let w = window.innerWidth;
        let h = window.innerHeight;
        let stars = [];
        let bands = [];

        // ————————————————————————————————
        // Resize handler: recalc canvas, stars & bands
        // ————————————————————————————————
        const handleResize = () => {
            w = window.innerWidth;
            h = window.innerHeight;
            canvas.width = w;
            canvas.height = h;

            // reduce complexity on small screens
            bands = w < CONFIG.mobileBreakpoint
                ? CONFIG.bands.slice(0, 2)
                : CONFIG.bands;

            const starCount = w < CONFIG.mobileBreakpoint
                ? CONFIG.starCountSmall
                : CONFIG.starCountLarge;
            stars = generateStars(w, h, starCount);
        };
        handleResize();
        window.addEventListener("resize", handleResize);

        // ————————————————————————————————
        // Draw one frame (shared by static & animate)
        // ————————————————————————————————
        const drawFrame = (t = 0) => {
            ctx.clearRect(0, 0, w, h);

            // 1) Night sky base
            ctx.fillStyle = "#050A12";
            ctx.fillRect(0, 0, w, h);

            // 2) Stars behind
            stars.forEach((star, i) => {
                const twinkle = Math.abs(Math.sin(t * star.tw + i)) * 0.6 + 0.2;
                ctx.globalAlpha = star.baseAlpha * twinkle;
                ctx.beginPath();
                ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
                ctx.fillStyle = "#fff";
                ctx.fill();
            });

            // 3) Horizon glow
            const hg = ctx.createLinearGradient(0, h * 0.8, 0, h);
            hg.addColorStop(0, CONFIG.horizon.start);
            hg.addColorStop(1, CONFIG.horizon.end);
            ctx.globalAlpha = 1;
            ctx.fillStyle = hg;
            ctx.fillRect(0, h * 0.8, w, h * 0.2);

            // 4) Aurora bands
            bands.forEach((band, idx) => {
                ctx.save();
                ctx.filter = `blur(${band.blur}px)`;

                // horizontal gradient flowing across canvas
                const grad = ctx.createLinearGradient(0, 0, w, 0);
                grad.addColorStop(0, band.colorStart);
                grad.addColorStop(1, band.colorEnd);
                ctx.fillStyle = grad;

                for (let x = 0; x < w; x += CONFIG.sliceWidth) {
                    // centre y at this slice
                    const yC1 = computeBandY(band, x, t, idx, h);
                    const yC2 = computeBandY(band, x + CONFIG.sliceWidth, t, idx, h);

                    // local opacity variation → gaps
                    const noiseVal = simplex.noise2D(
                        x * band.freq + (t * band.speed + band.phase),
                        idx + 13
                    );
                    ctx.globalAlpha = band.alpha * (0.5 + 0.5 * noiseVal);

                    // draw a thin trapezoid of height=thickness
                    const top1 = yC1 - band.thickness * 0.5;
                    const top2 = yC2 - band.thickness * 0.5;
                    const bot1 = top1 + band.thickness;
                    const bot2 = top2 + band.thickness;

                    ctx.beginPath();
                    ctx.moveTo(x, top1);
                    ctx.lineTo(x + CONFIG.sliceWidth, top2);
                    ctx.lineTo(x + CONFIG.sliceWidth, bot2);
                    ctx.lineTo(x, bot1);
                    ctx.closePath();
                    ctx.fill();
                }

                ctx.restore();
            });

            // 5) Occlude stars in band areas (twice—on top—for realistic peeking)
            stars.forEach((star, i) => {
                // find each band’s centre at this x
                let occ = 1;
                bands.forEach((band, idx) => {
                    const yC = computeBandY(band, star.x, t, idx, h);
                    if (Math.abs(star.y - yC) < band.thickness * 0.5) {
                        occ *= (1 - band.alpha); // hide behind opaque parts
                    }
                });

                if (occ < 0.99) {
                    const twinkle = Math.abs(Math.sin(t * star.tw + i)) * 0.6 + 0.2;
                    ctx.globalAlpha = star.baseAlpha * twinkle * occ;
                    ctx.beginPath();
                    ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
                    ctx.fillStyle = "#fff";
                    ctx.fill();
                }
            });
        };

        // ————————————————————————————————
        // Animation loop
        // ————————————————————————————————
        let rafId;
        const animate = (ts = 0) => {
            drawFrame(ts / 1000);
            rafId = requestAnimationFrame(animate);
        };

        if (prefersReduced) {
            drawFrame(0);
        } else {
            animate(0);
        }

        // cleanup
        return () => {
            window.removeEventListener("resize", handleResize);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isDarkMode]);

    if (!isDarkMode) return null; // nothing in light mode

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 w-full h-full pointer-events-none z-0"
            style={{ objectFit: "cover" }}
            aria-hidden="true"
        />
    );
};

export default AuroraBackground;
